import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import MyStatsSkeletonLoader from './MyStatsSkeletonLoader';

// MyStats component displays the user's statistics, including level, XP, and various performance metrics.
function MyStats() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedStats, setAnimatedStats] = useState({
    xp: 0,
    total_games_played: 0,
    total_holes_played: 0,
    total_shots_taken: 0,
    average_score_per_round: 0,
    average_shots_per_hole: 0,
    total_hole_in_ones: 0,
    hole_in_one_percentage: 0,
    over_under_par: 0,
  });

  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  // Fetch stats from the database
  useEffect(() => {
    if (user) {
      setLoading(true);
      fetch(`/server/crazy-cup-server/getUserStats.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerk_user_id: user.id }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setStats(data.stats);
            startCountUpAnimation(data.stats);
          } else {
            console.error('Error fetching stats:', data.message);
          }
        })
        .catch((error) => console.error('Error fetching stats:', error))
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 200);
        });
    }
  }, [user]);

  // Function to start the count-up animation
  const startCountUpAnimation = (targetStats) => {
    const duration = 3000;
    const frameRate = 60;
    const totalFrames = Math.round((duration / 1000) * frameRate);

    Object.keys(targetStats).forEach((key) => {
      if (typeof targetStats[key] === 'number') {
        let frame = 0;
        const interval = setInterval(() => {
          frame++;
          const progress = frame / totalFrames;
          const easedProgress = easeOutQuad(progress);
          const currentValue = Math.round(targetStats[key] * easedProgress);

          setAnimatedStats((prev) => ({
            ...prev,
            [key]: currentValue,
          }));

          if (frame === totalFrames) {
            clearInterval(interval);
          }
        }, 1000 / frameRate);
      }
    });
  };

  // Easing function for smooth animation
  const easeOutQuad = (t) => t * (2 - t);

  // Calculate XP progress percentage
  const xpProgress = stats ? (stats.xp / stats.xp_to_next_level) * 100 : 0;

  return (
    <div id="stats-page" className="page">
      <h1 className='page-title'>MY<br></br>STATS</h1>
      {loading ? (
        <MyStatsSkeletonLoader/>
      ) : stats && stats.total_games_played < 1 ? (
        <p>No rounds played.</p>
      ) : (
        <>
          {/* Display Level, XP, and XP to Next Level */}
          <div className="user-level-info">
            <p>Level <strong>{stats.level}</strong></p>
            <p>XP: <strong>{animatedStats.xp}</strong> / <strong>{stats.xp_to_next_level}</strong></p>
          </div>

          {/* XP Progress Bar */}
          <div
            className="loaderBar"
            style={{ '--progress': `${xpProgress}%` }}
          ></div>

          {/* Display Stats Grid */}
          <div className="stats-grid-container">
            <div className="stats-grid">
              <div><span className='stat-figure'>{animatedStats.total_games_played}</span><br></br>Total Rounds</div>
              <div><span className='stat-figure'>{animatedStats.total_holes_played}</span><br></br>Total Holes</div>
              <div><span className='stat-figure'>{animatedStats.total_shots_taken}</span><br></br>Total Shots</div>
              <div><span className='stat-figure'>{animatedStats.average_score_per_round.toFixed(1)}</span><br></br>Round Avg</div>
              <div><span className='stat-figure'>{animatedStats.average_shots_per_hole.toFixed(1)}</span><br></br>Hole Avg</div>
              <div><span className='stat-figure'>{animatedStats.total_hole_in_ones}</span><br></br>Hole-in-Ones</div>
              <div><span className='stat-figure'>{Math.round(animatedStats.hole_in_one_percentage)}%</span><br></br>Hole-in-One %</div>
              <div>
                <span className='stat-figure'>
                  {animatedStats.over_under_par > 0
                    ? `+${animatedStats.over_under_par}`
                    : animatedStats.over_under_par < 0
                    ? `${animatedStats.over_under_par}`
                    : `Even`}
                </span>
                <br></br>
                {animatedStats.over_under_par > 0
                  ? "Over Par"
                  : animatedStats.over_under_par < 0
                  ? "Under Par"
                  : "Par"}
              </div>
            </div>
          </div>
        </>
      )}
      {!loading && (
        <button id="back-to-main" className="back-to-main" onClick={() => navigate('/')}>
          Back
        </button>
      )}
    </div>
  );
}

export default MyStats;